'use client'

import { giphyWhite } from '@giphy/colors'
import CreationToolCutOutMode from 'creation/src/creation-tools/types/cut-out-mode'
import { useEffect, useRef } from 'react'
import NumberInput from 'ui/src/components/controls/number-input'
import Slider, { SliderRef } from 'ui/src/components/controls/slider'
import { useCutOut } from './cut-out-context'
import { POINT_SIZE_SCALE } from './utils'

type PenSizeToolProps = {
    title: string
    description: string
    penValue: number
    onChange: (value: number) => void
    mode: CreationToolCutOutMode
}

const PenSizeTool = ({ title, description, penValue, onChange, mode }: PenSizeToolProps) => {
    const sliderRef = useRef<SliderRef>(null)
    const convertedPenValue = Math.round(Number((penValue * POINT_SIZE_SCALE).toFixed(2)))
    const penPreviewSize = 0.03 * convertedPenValue + 0.47

    useEffect(() => {
        sliderRef.current?.setValue?.(penValue)
    }, [mode])

    return (
        <div className="w-full rounded-[4px] bg-giphyBlack px-[15px] py-[30px]">
            <h3 className="mx-[10px] text-[15px] font-bold">{title}</h3>
            <div className="mx-[10px] my-[5px] flex items-center justify-between">
                <h4 className="text-[15px] font-normal text-giphyWhite">{description}</h4>
                <NumberInput
                    color={giphyWhite}
                    extension="px"
                    size="large"
                    value={convertedPenValue}
                    onChangeValue={(value) => {
                        if (value) {
                            const finalValue = Number((value / POINT_SIZE_SCALE).toFixed(2))
                            onChange(finalValue)
                            sliderRef.current?.setValue?.(finalValue)
                        }
                    }}
                    max={40}
                />
            </div>
            <div className="mt-[20px] rounded-[18px] bg-giphyDarkestGrey px-[20px] py-[2px]">
                <Slider
                    ref={sliderRef}
                    defaultValue={penValue}
                    onSliderValueChange={(value: number) => {
                        if (value > 0) {
                            onChange(Number(value.toFixed(2)))
                        } else {
                            onChange(0.02)
                        }
                    }}
                    customHandleTransform={`scale(${penPreviewSize})`}
                />
            </div>
        </div>
    )
}

export const ToolConfig = () => {
    const { eraseSize, setEraseSize, brushSize, setBrushSize, mode } = useCutOut()

    switch (mode) {
        case CreationToolCutOutMode.Erase:
            return (
                <PenSizeTool
                    title="Adjust Eraser Size"
                    description="Make the eraser smaller or larger."
                    penValue={eraseSize}
                    onChange={setEraseSize}
                    mode={mode}
                />
            )
        case CreationToolCutOutMode.Paint:
            return (
                <PenSizeTool
                    title="Adjust Brush Size"
                    description="Shrink or grow your the size of your brush."
                    penValue={brushSize}
                    onChange={setBrushSize}
                    mode={mode}
                />
            )
        default:
            return null
    }
}

export default PenSizeTool
